import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../components/utils/api';
import { SellListingLike } from '../components/utils/priceEstimation';

export type SellListing = SellListingLike;

/**
 * Fetch the logged-in user's sell listings (raw /api/user/sell-listings shape)
 * for the Tools flows. Fetches when `enabled` flips true; exposes retry.
 * `fetched` is true only once the CURRENT enable's request has completed —
 * callers must gate auto-selection on it, or a reopened flow could act on the
 * previous open's stale listings before the fresh fetch lands.
 */
export function useSellListings(enabled: boolean): {
  listings: SellListing[];
  loading: boolean;
  error: boolean;
  fetched: boolean;
  retry: () => void;
} {
  const [listings, setListings] = useState<SellListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  useEffect(() => {
    if (!enabled) {
      setFetched(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setFetched(false);
    setListings([]);
    apiRequest('/api/user/sell-listings')
      .then((res) => {
        if (!cancelled) setListings(res.listings || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setFetched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  return { listings, loading, error, fetched, retry };
}
